import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import EditButtonIcon from 'src/assets/img/edit-button-icon.svg';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { emailScheme } from 'src/utils/validation-schemes';
import Input from 'src/components/Input/Input';
import { useUpdateAccountsMutation } from 'src/services/accountsService';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { Button } from 'src/components/ui/button';

interface EmailForInvoicePopupProps {
  disabled?: boolean;
  defaultEmail?: string;
}

export const EmailForInvoicePopup = ({
  disabled = false,
  defaultEmail = '',
}: EmailForInvoicePopupProps) => {
  const [open, setOpen] = useState(false);

  const notification = useNotification();
  const loader = useLoader();

  const [updateAccountInfo, { isLoading: isLoadingUpdateAccountInfo, isSuccess }] =
    useUpdateAccountsMutation();

  useEffect(() => {
    if (isLoadingUpdateAccountInfo) {
      loader.show();
    } else {
      setOpen(false);
      loader.hide();
    }
  }, [isLoadingUpdateAccountInfo]);

  useEffect(() => {
    if (isSuccess) {
      notification.success('Email updated successfully!');
    }
  }, [isSuccess]);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      email: defaultEmail,
    },
    resolver: yupResolver(
      object({
        email: emailScheme({ required: true }).label('New email'),
      }),
    ),
  });

  const watchEmail = watch('email');

  const submit = (data: { email: string }) => {
    updateAccountInfo({ email_for_invoices: data.email });
  };

  useEffect(() => {
    if (isSuccess) {
      reset({ email: watchEmail });
    }
  }, [isSuccess]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (open) {
      timeout = setTimeout(() => {
        (document.activeElement as HTMLElement)?.blur();
      }, 0);
    }

    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [open]);

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        <Button variant="pill-outline" size="pill" className="card-header-button">
          <span>Edit</span>
          <img src={EditButtonIcon} alt="edit icon" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Email for invoices</DialogTitle>
        </DialogHeader>
        <form>
          <div className="content">
            <Controller
              control={control}
              name="email"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  type={'text'}
                  label={'New email for invoices'}
                  placeholder={'example@example.com'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                />
              )}
            />
          </div>
        </form>
        <div className="footer flex justify-end mt-5">
          <div className="help-container"></div>
          <div className="action-buttons flex gap-2.5">
            <Button
              variant="pill-outline-dark"
              size="pill"
              className="min-w-28"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="pill-green"
              size="pill"
              disabled={!isDirty || isLoadingUpdateAccountInfo}
              onClick={handleSubmit(submit)}
              type="submit"
              form="edit-user-form"
              className="min-w-28"
            >
              {isLoadingUpdateAccountInfo ? 'Loading...' : 'Done'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
